import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString();
}

function hasChanges(row) {
  return String(row?.draftCronExpression || '').trim() !== String(row?.cronExpression || '').trim();
}

function SystemTasksPageView({
  rows,
  isLoading,
  isSaving,
  savingTask,
  savingAction,
  error,
  success,
  formErrors,
  onCronExpressionChange,
  resetTask,
  saveTask,
  toggleTaskStatus,
}) {
  const { t } = useTranslation();
  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('systemTasks.savingInfo') : ''}
        error={error}
        success={success}
      />
      <PageHeader title={t('systemTasks.title')} subtitle={t('systemTasks.subtitle')} />

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <table className="table search-table align-middle mb-0">
              <thead className="header-item">
                <tr>
                  <th style={{ width: '220px' }}>{t('systemTasks.colTask')}</th>
                  <th style={{ width: '220px' }}>{t('systemTasks.colLabel')}</th>
                  <th style={{ width: '110px' }}>{t('systemTasks.colStatus')}</th>
                  <th>{t('systemTasks.colCronExpression')}</th>
                  <th style={{ width: '180px' }}>{t('systemTasks.colNextRun')}</th>
                  <th style={{ width: '180px' }}>{t('systemTasks.colLastRun')}</th>
                  <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('systemTasks.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">{t('systemTasks.noTasksFound')}</td>
                  </tr>
                ) : null}

                {rows.map((row) => {
                  const rowErrors = formErrors[row.task] || {};
                  const rowIsSaving = savingTask === row.task;
                  const rowHasChanges = hasChanges(row);
                  const statusButtonLabel = row.isActive ? t('systemTasks.deactivateButton') : t('systemTasks.activateButton');

                  return (
                    <tr key={row.task}>
                      <td className="font-monospace text-break">{row.task}</td>
                      <td>{row.label || '-'}</td>
                      <td>
                        <span className={`badge ${row.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                          {row.isActive ? t('systemTasks.statusActive') : t('systemTasks.statusInactive')}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className={`form-control font-monospace ${rowErrors.cronExpression ? 'is-invalid' : ''}`}
                          value={row.draftCronExpression}
                          onChange={(event) => onCronExpressionChange(row.task, event.target.value)}
                          disabled={isLoading || isSaving}
                          placeholder="0 * * * *"
                        />
                        {rowErrors.cronExpression ? (
                          <div className="invalid-feedback d-block">{rowErrors.cronExpression}</div>
                        ) : null}
                      </td>
                      <td>{formatDateTime(row.nextRunAt)}</td>
                      <td>{formatDateTime(row.lastRunAt)}</td>
                      <td className="text-end text-nowrap">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className={`btn btn-sm ${row.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={() => toggleTaskStatus(row.task)}
                            disabled={isLoading || isSaving}
                          >
                            {rowIsSaving && savingAction === 'status'
                              ? `${statusButtonLabel}...`
                              : statusButtonLabel}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-default"
                            onClick={() => resetTask(row.task)}
                            disabled={isLoading || isSaving || !rowHasChanges}
                          >
                            {t('systemTasks.resetButton')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => saveTask(row.task)}
                            disabled={isLoading || isSaving || !rowHasChanges}
                          >
                            {rowIsSaving && savingAction === 'schedule' ? t('systemTasks.savingInfo') : t('systemTasks.saveButton')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default SystemTasksPageView;
